import React, { useState, useEffect } from 'react';
import { listCalculatorConfigs, saveCalculatorConfig, deleteDocument } from '../../../services/contentApi';

const CalculatorsManager = () => {
  const [calculators, setCalculators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCalculator, setEditingCalculator] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadCalculators();
  }, []);

  const loadCalculators = async () => {
    try {
      const data = await listCalculatorConfigs();
      setCalculators(data);
    } catch (error) {
      console.error('Error loading calculators:', error);
      setMessage('Error loading calculators');
    } finally {
      setLoading(false);
    }
  };


  const handleAddCalculator = async (calculatorData) => {
    try {
      const id = `calc-${Date.now()}`;
      await saveCalculatorConfig(id, calculatorData);
      await loadCalculators();
      setShowAddModal(false);
      setMessage('Calculator added successfully!');
    } catch (error) {
      console.error('Error adding calculator:', error);
      setMessage('Error adding calculator');
    }
  };

  const handleEditCalculator = async (calculatorData) => {
    try {
      await saveCalculatorConfig(editingCalculator.id, calculatorData);
      await loadCalculators();
      setEditingCalculator(null);
      setMessage('Calculator updated successfully!');
    } catch (error) {
      console.error('Error updating calculator:', error);
      setMessage('Error updating calculator');
    }
  };

  const handleDeleteCalculator = async (calculatorId) => {
    setMessage('Permission Denied: Deleting calculators is currently restricted.');
  };

  const toggleCalculatorStatus = async (calculatorId) => {
    try {
      const calculator = calculators.find(c => c.id === calculatorId);
      if (calculator) {
        await saveCalculatorConfig(calculatorId, {
          ...calculator,
          isActive: !calculator.isActive
        });
        await loadCalculators();
        setMessage('Calculator status updated!');
      }
    } catch (error) {
      console.error('Error toggling calculator status:', error);
      setMessage('Error updating calculator status');
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const calculatorData = {
      name: formData.get('name'),
      description: formData.get('description'),
      formula: formData.get('formula'),
      variables: JSON.parse(formData.get('variables') || '[]'),
      resultLabel: formData.get('resultLabel'),
      resultUnit: formData.get('resultUnit')
    };

    if (editingCalculator) {
      handleEditCalculator(calculatorData);
    } else {
      handleAddCalculator(calculatorData);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading calculators...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Calculators Management</h2>
          <p className="text-gray-600">Manage interactive tools and formulas</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-custom-brown text-white px-4 py-2 rounded-lg hover:bg-custom-brown/90 transition-colors duration-200"
        >
          Add Calculator
        </button>
      </div>

      {message && (
        <div className={`p-4 rounded-lg ${message.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
          {message}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['overview', 'formulas', 'usage'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${activeTab === tab
                ? 'border-custom-brown text-custom-brown'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {calculators.map((calculator) => (
            <div key={calculator.id} className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{calculator.name}</h3>
                <span className={`px-2 py-1 text-xs rounded-full ${calculator.isActive
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
                  }`}>
                  {calculator.isActive ? 'active' : 'inactive'}
                </span>
              </div>
              <p className="text-gray-600 mb-4">{calculator.description}</p>
              <div className="text-sm text-gray-500 mb-4">
                <p>Usage: {calculator.usage || 0} times</p>
                <p>Variables: {calculator.inputs?.length || 0}</p>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => setEditingCalculator(calculator)}
                  className="flex-1 bg-custom-brown/10 text-custom-brown px-3 py-2 rounded text-sm hover:bg-custom-brown/20 hover:text-custom-brown transition-colors duration-200"
                >
                  Edit
                </button>
                <button
                  onClick={() => toggleCalculatorStatus(calculator.id)}
                  className={`flex-1 px-3 py-2 rounded text-sm transition-colors duration-200 ${calculator.isActive
                    ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                >
                  {calculator.isActive ? 'Disable' : 'Enable'}
                </button>
                {/* Delete button removed as per permission restriction */}
                {/* <button
                  onClick={() => handleDeleteCalculator(calculator.id)}
                  className="flex-1 bg-red-50 text-red-600 px-3 py-2 rounded text-sm hover:bg-red-100 hover:text-red-700 transition-colors duration-200"
                >
                  Delete
                </button> */}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'formulas' && (
        <div className="space-y-4">
          {calculators.map((calculator) => (
            <div key={calculator.id} className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">{calculator.name}</h3>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Description:</p>
                <p className="text-gray-900">{calculator.description}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Inputs:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {calculator.inputs?.map((input, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded p-3">
                      <p className="font-medium text-gray-900">{input.label}</p>
                      <p className="text-sm text-gray-500">Type: {input.type}</p>
                      {input.unit && <p className="text-sm text-gray-500">Unit: {input.unit}</p>}
                    </div>
                  )) || <p className="text-gray-500">No inputs defined</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'usage' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Calculator Usage Analytics</h3>
          <div className="space-y-4">
            {calculators.map((calculator) => (
              <div key={calculator.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">{calculator.name}</h4>
                  <p className="text-sm text-gray-500">{calculator.usage} total uses</p>
                </div>
                <div className="text-right">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-custom-brown h-2 rounded-full"
                      style={{ width: `${Math.min((calculator.usage / 250) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {Math.round((calculator.usage / 250) * 100)}% of max
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add/Edit Calculator Modal */}
      {(showAddModal || editingCalculator) && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAddModal(false);
              setEditingCalculator(null);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh] border border-gray-200">
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-900">
                {editingCalculator ? 'Edit Calculator' : 'Add New Calculator'}
              </h3>
            </div>

            <form onSubmit={handleFormSubmit} className="flex flex-col min-h-0 flex-1">
              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Calculator Name
                  </label>
                  <input
                    name="name"
                    type="text"
                    defaultValue={editingCalculator?.name || ''}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    rows="3"
                    defaultValue={editingCalculator?.description || ''}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Formula
                  </label>
                  <textarea
                    name="formula"
                    rows="2"
                    defaultValue={editingCalculator?.formula || ''}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors font-mono"
                    placeholder="e.g., FSI = Total Built-up Area / Plot Area"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Variables (JSON format)
                  </label>
                  <textarea
                    name="variables"
                    rows="6"
                    defaultValue={JSON.stringify(editingCalculator?.variables || [], null, 2)}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors font-mono"
                    placeholder='[{"name": "plotArea", "label": "Plot Area", "type": "number", "unit": "sq ft"}]'
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Result Label
                    </label>
                    <input
                      name="resultLabel"
                      type="text"
                      defaultValue={editingCalculator?.resultLabel || ''}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                      placeholder="e.g., FSI Value"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Result Unit
                    </label>
                    <input
                      name="resultUnit"
                      type="text"
                      defaultValue={editingCalculator?.resultUnit || ''}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:outline-none focus:border-custom-brown focus:ring-1 focus:ring-custom-brown transition-colors"
                      placeholder="e.g., sq ft"
                    />
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-gray-200 bg-gray-50 flex gap-3 flex-shrink-0 rounded-b-lg">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingCalculator(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-custom-brown text-white px-4 py-2 rounded-md hover:bg-custom-brown/90 transition-colors flex items-center justify-center font-medium shadow-sm"
                >
                  {editingCalculator ? 'Update' : 'Add'} Calculator
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalculatorsManager; 